import { Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { TaskUserModel } from '../../tasks/models/TaskUserModel';
import { LeadsWindowModel } from '../models/LeadsWindowModel';
import { createLeadsWindow } from '../services/createLeadsWindow';
import { updateLeadsWindow } from '../services/updateLeadsWindow';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  agents: Array<TaskUserModel>;
  editingEntry: LeadsWindowModel | null;
}

const INITIAL_VALUES = {
  shift: 'morning' as LeadsWindowModel['shift'],
  from_date: '',
  to_date: '',
  sale_agent: null as number | null,
};

export default function ModalLeadsWindowForm({
  visible,
  onClose,
  onSuccess,
  agents,
  editingEntry,
}: Props) {
  const intl = useIntl();
  const [values, setValues] = useState(INITIAL_VALUES);
  const [loading, setLoading] = useState(false);
  const isEditing = !!editingEntry;

  useEffect(() => {
    if (editingEntry) {
      setValues({
        shift: editingEntry.shift,
        from_date: editingEntry.from_date,
        to_date: editingEntry.to_date,
        sale_agent: editingEntry.sale_agent,
      });
    } else {
      setValues(INITIAL_VALUES);
    }
  }, [editingEntry, visible]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!values.sale_agent) return;

    const body = {
      shift: values.shift,
      from_date: values.from_date,
      to_date: values.to_date,
      sale_agent: values.sale_agent,
    };

    try {
      setLoading(true);
      if (isEditing) {
        await updateLeadsWindow(editingEntry.id, body);
        toast.success(intl.formatMessage({ id: 'leadsWindows.updateSuccess' }));
      } else {
        await createLeadsWindow(body);
        toast.success(intl.formatMessage({ id: 'leadsWindows.createSuccess' }));
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      const message = detail
        ? String(detail)
        : intl.formatMessage({
            id: isEditing
              ? 'leadsWindows.updateError'
              : 'leadsWindows.createError',
          });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }

  const selectedAgent = agents.find((a) => a.id === values.sale_agent);

  return (
    <Modal
      show={visible}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({
            id: isEditing
              ? 'leadsWindows.editTitle'
              : 'leadsWindows.createTitle',
          })}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="mb-4">
            <label htmlFor="sale_agent" className="form-label">
              {intl.formatMessage({ id: 'leadsWindows.agent' })}
            </label>
            <Select
              id="sale_agent"
              value={
                selectedAgent
                  ? {
                      value: selectedAgent.id,
                      label: `${selectedAgent.name} (${selectedAgent.email})`,
                    }
                  : null
              }
              options={agents.map((agent) => ({
                value: agent.id,
                label: `${agent.name} (${agent.email})`,
              }))}
              placeholder={intl.formatMessage({
                id: 'leadsWindows.selectAgent',
              })}
              isClearable
              isSearchable
              onChange={(option) =>
                setValues({ ...values, sale_agent: option?.value ?? null })
              }
            />
          </div>
          <div className="mb-4">
            <label htmlFor="shift" className="form-label">
              {intl.formatMessage({ id: 'leadsWindows.shift' })}
            </label>
            <select
              id="shift"
              className="form-select"
              value={values.shift}
              onChange={(e) =>
                setValues({
                  ...values,
                  shift: e.target.value as LeadsWindowModel['shift'],
                })
              }
              required
            >
              <option value="morning">
                {intl.formatMessage({ id: 'leadsWindows.shiftMorning' })}
              </option>
              <option value="afternoon">
                {intl.formatMessage({ id: 'leadsWindows.shiftAfternoon' })}
              </option>
            </select>
          </div>
          <div className="row">
            <div className="mb-4 col-12 col-md-6">
              <label htmlFor="from_date" className="form-label">
                {intl.formatMessage({ id: 'leadsWindows.fromDate' })}
              </label>
              <input
                type="date"
                className="form-control"
                id="from_date"
                value={values.from_date}
                onChange={(e) =>
                  setValues({ ...values, from_date: e.target.value })
                }
                required
              />
            </div>
            <div className="mb-4 col-12 col-md-6">
              <label htmlFor="to_date" className="form-label">
                {intl.formatMessage({ id: 'leadsWindows.toDate' })}
              </label>
              <input
                type="date"
                className="form-control"
                id="to_date"
                value={values.to_date}
                onChange={(e) =>
                  setValues({ ...values, to_date: e.target.value })
                }
                required
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {intl.formatMessage({ id: 'common.cancel' })}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !values.sale_agent}
          >
            {loading
              ? intl.formatMessage({ id: 'leadsWindows.saving' })
              : intl.formatMessage({ id: 'leadsWindows.save' })}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
